import { useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useAppDispatch, useAppSelector } from './redux'
import {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  sessionRestoreStart,
  sessionRestoreSuccess,
  sessionRestoreFailure
} from '@/store/authSlice'
import { authService, authManager } from '@/services/authService'
import { tokenStorage } from '@/services/tokenStorage'
import { sessionStorage } from '@/utils/sessionStorage'

export const useAuth = () => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const { isAuthenticated, user, token, loading, error, sessionRestored } = useAppSelector((state) => state.auth)
  const initializingRef = useRef(false)

  // Clear schedule cache when user changes
  const clearScheduleCache = useCallback(() => {
    queryClient.removeQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && (key[0] === 'schedule' || key[0] === 'conflicts' || key[0] === 'conflicts-calendar');
      }
    });
  }, [queryClient]);

  /**
   * Restore session on app initialization
   * Uses the new dual-token system with IndexedDB storage
   */
  const restoreSession = useCallback(async () => {
    // Prevent duplicate initialization
    if (initializingRef.current) {
      return false
    }
    initializingRef.current = true

    dispatch(sessionRestoreStart())

    try {
      // Initialize token storage (IndexedDB)
      await tokenStorage.initialize()

      // Check for quick session restore from localStorage (user data only)
      const localSession = sessionStorage.getSession()

      // Check for refresh token in IndexedDB
      const refreshToken = await tokenStorage.getRefreshToken()

      if (!refreshToken && !localSession) {
        // No session to restore
        dispatch(sessionRestoreFailure('No session found'))
        return false
      }

      // If we have a refresh token, use authManager to restore session
      // This ensures all refresh attempts go through the central mutex
      if (refreshToken) {
        try {
          const authState = await authManager.initialize()

          if (authState.isAuthenticated && authState.user && authState.accessToken) {
            dispatch(sessionRestoreSuccess({
              token: authState.accessToken,
              refreshToken: await tokenStorage.getRefreshToken() || '',
              user: authState.user
            }))
            clearScheduleCache()
            return true
          }
        } catch (error) {
          console.warn('Token refresh failed:', error)
          // Fall through to try local session
        }
      }

      // Fallback: try to restore from localStorage session
      if (localSession?.user && localSession?.token) {
        // Validate the token by calling refresh endpoint
        try {
          const response = await authService.refresh()

          // Update authManager so API interceptor has access to the token
          if (response.refreshToken && response.expiresIn) {
            await authManager.updateSession(
              response.accessToken || response.token,
              response.expiresIn,
              response.refreshToken,
              response.user
            )
          }

          dispatch(sessionRestoreSuccess({
            token: response.accessToken || response.token,
            refreshToken: response.refreshToken,
            user: response.user
          }))
          clearScheduleCache()
          return true
        } catch (error) {
          console.warn('Session validation failed:', error)
        }
      }

      // Session restoration failed
      sessionStorage.clearSession()
      await tokenStorage.clearTokens()
      dispatch(sessionRestoreFailure('Session expired'))
      return false
    } catch (error) {
      console.error('Session restoration error:', error)
      dispatch(sessionRestoreFailure('Session restoration failed'))
      return false
    } finally {
      initializingRef.current = false
    }
  }, [dispatch, clearScheduleCache])

  /**
   * Login with credentials
   */
  const login = useCallback(async (email: string, password: string) => {
    try {
      dispatch(loginStart())
      const response = await authService.login({ email, password })

      // Store tokens
      if (response.refreshToken) {
        await tokenStorage.storeRefreshToken(response.refreshToken)
      }

      // Update authManager so API interceptor has access to the token
      if (response.refreshToken && response.expiresIn) {
        await authManager.updateSession(
          response.accessToken || response.token,
          response.expiresIn,
          response.refreshToken,
          response.user
        )
      }

      // Save session with user data for quick restore
      sessionStorage.saveSession(
        response.accessToken || response.token,
        response.user
      )

      dispatch(loginSuccess({
        token: response.accessToken || response.token,
        refreshToken: response.refreshToken,
        user: response.user
      }))
      clearScheduleCache()
      return { success: true }
    } catch (error: any) {
      let message: string;
      if (!error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')) {
        // Backend is completely unreachable (e.g. still starting up)
        message = 'Unable to reach the server. Please check your connection and try again in a moment.';
      } else if (error.response?.status === 503) {
        message = error.response?.data?.error?.message || 'The service is temporarily unavailable. Please try again in a moment.';
      } else {
        message = error.response?.data?.error?.message || error.response?.data?.message || 'Login failed';
      }
      dispatch(loginFailure(message))
      return { success: false, error: message }
    }
  }, [dispatch, clearScheduleCache])

  /**
   * Logout and clear session
   */
  const logoutUser = useCallback(async () => {
    try {
      // Get refresh token before clearing
      const refreshToken = await tokenStorage.getRefreshToken()
      await authService.logout(refreshToken || undefined)
    } catch (error) {
      console.warn('Logout request failed:', error)
    } finally {
      sessionStorage.clearSession()
      await tokenStorage.clearTokens()
      dispatch(logout())
      clearScheduleCache()
    }
  }, [dispatch, clearScheduleCache])

  /**
   * Extend current session (for backward compatibility)
   * With the new token system, this is handled automatically
   */
  const extendSession = useCallback(() => {
    if (isAuthenticated) {
      sessionStorage.extendSession()
    }
  }, [isAuthenticated])

  /**
   * Check if session is about to expire (within 1 hour)
   */
  const isSessionExpiringSoon = useCallback(() => {
    const timeUntilExpiry = sessionStorage.getTimeUntilExpiry()
    return timeUntilExpiry > 0 && timeUntilExpiry < 60 * 60 * 1000 // 1 hour
  }, [])

  /**
   * Auto-extend localStorage session on user activity
   * (Token refresh is handled automatically by authManager)
   */
  useEffect(() => {
    if (!isAuthenticated) return

    const handleUserActivity = () => {
      if (isSessionExpiringSoon()) {
        extendSession()
      }
    }

    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true })
    })

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity)
      })
    }
  }, [isAuthenticated, extendSession, isSessionExpiringSoon])

  return {
    // State
    isAuthenticated,
    user,
    token,
    loading,
    error,
    sessionRestored,

    // Actions
    login,
    logout: logoutUser,
    restoreSession,
    extendSession,

    // Utilities
    isSessionExpiringSoon,
    timeUntilExpiry: sessionStorage.getTimeUntilExpiry()
  }
}
